import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as forge from 'node-forge';
import { Router } from '@angular/router';
import { SettingsService } from './settings.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SignatureVerifierService {
  private http = inject(HttpClient);
  private settingsService = inject(SettingsService);
  private readonly PUBLIC_KEY = this.settingsService.publicKey;
  private router = inject(Router);

  /**
   * Retrieves a JSON resource, validates its integrity via an attached digital signature,
   * and returns the typed response body.
   *
   * Workflow:
   * 1. Performs an HTTP GET request to the provided URL (response observed for headers + body).
   * 2. Applies the 'ngsw-bypass' header to avoid Angular Service Worker caching.
   * 3. Extracts all response headers and looks for a signature in either `X-Signature` or `x-signature`.
   * 4. Verifies the signature against the response body using the configured public key.
   * 5. On signature failure:
   *    - Navigates the router to `/unauthorized`
   *    - Flags `settingsService.isLoadData` as true
   *    - Returns a placeholder (`null` cast to `T`)
   *
   * Side Effects:
   * - May trigger a route navigation on invalid signature.
   * - Mutates application state through `settingsService.isLoadData`.
   *
   * Security:
   * - Ensures payload integrity before exposing data to the application.
   * - Only a successfully verified body is returned to the caller.
   *
   * @typeParam T - Expected shape of the response body.
   * @param url - Absolute or relative endpoint from which to fetch the resource.
   * @returns A promise resolving to the validated response body as `T`; returns a `null` placeholder (typed as `T`) after redirect if signature verification fails.
   */
  async get<T>(url: string): Promise<T> {
    const widgetData$ = this.http.get<T>(`${url}`, {
      observe: 'response',
      headers: { 'ngsw-bypass': 'true' },
    });

    const configData = await firstValueFrom(widgetData$);

    const exposedHeaders: Record<string, string | null> = {};
    for (const k of configData.headers.keys()) {
      exposedHeaders[k] = configData.headers.get(k);
    }
    const signatureHeader =
      configData.headers.get('X-Signature') ||
      configData.headers.get('x-signature') ||
      null;

    const isValid = this.verifySignature(
      configData.body,
      signatureHeader as string,
      this.PUBLIC_KEY(),
    );
    if (!isValid) {
      this.router.navigate(['/unauthorized']);
      this.settingsService.isLoadData.set(true);
      return null as unknown as T;
    }

    return configData.body as T;
  }

  /**
   * Verifies a SHA-256 + RSA signature over a JSON-serialized payload.
   *
   * The function stringifies the provided data object with JSON.stringify,
   * computes a SHA-256 digest of the resulting UTF-8 string, and uses the
   * supplied PEM-encoded RSA public key to verify the given Base64-encoded
   * signature bytes.
   *
   * Important:
   * - Property order and serialization differences will affect the digest.
   *   Ensure the signer used the same JSON serialization strategy.
   * - Returns false if the public key PEM cannot be parsed or if the
   *   signature fails verification—no exceptions are propagated.
   * - Assumes the signature was produced with the corresponding private key
   *   using the same hash (SHA-256) and RSA signature scheme (forge default:
   *   PKCS#1 v1.5).
   *
   * @param data Arbitrary data structure that was originally signed; it will be JSON.stringified.
   * @param signatureBase64 The Base64-encoded signature string produced over the JSON payload's SHA-256 digest.
   * @param publicKeyPem A PEM-formatted RSA public key (e.g., "-----BEGIN PUBLIC KEY----- ... -----END PUBLIC KEY-----").
   * @returns true if signature verification succeeds; false otherwise (including parse errors).
   *
   * @example
   * const isValid = verifySignature(payload, receivedSignature, publicKeyPem);
   * if (!isValid) {  handle invalid signature  }
   *
   * @see forge.pki.publicKeyFromPem
   */

  verifySignature(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    signatureBase64: string,
    publicKeyPem: string,
  ): boolean {
    const json = JSON.stringify(data);
    const md = forge.md.sha256.create();
    md.update(json, 'utf8');
    const digestBytes = md.digest().bytes();

    let publicKey;
    try {
      publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
    } catch (error) {
      console.error('Failed to parse public key:', error);
      return false;
    }

    const signatureBytes = forge.util.decode64(signatureBase64);

    const verified = publicKey.verify(digestBytes, signatureBytes);

    return verified;
  }
}
